/* eslint-disable */
import {
  FC,
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
} from "react";
import { LayoutSplashScreen } from "../../../../_stream/layout/core";
import { AuthModel, UserModel } from "./_models";
import * as authHelper from "./AuthHelpers";
import { WithChildren } from "../../../../_stream/helpers";
import { jwtDecode } from "jwt-decode";
import {
  getConversionRates,
  getPermissions,
  switchBranch,
  switchOrganization,
  acceptInviteAsExistingUser,
} from "./_requests";
import {
  isRefreshBackoffActive,
  performTokenRefresh,
  persistAuthTokens,
} from "./tokenRefresh";
import { RootContext } from "../../../../store";
import dayjs from "dayjs";
import {
  trackMerchant,
  MerchantEvents,
  reset,
} from "../../../../_stream/helpers/analytics";
import {
  readStoredCurrentBranch,
  writeStoredCurrentBranch,
} from "./branchSession";
import { hydrateReachableBranches } from "./hydrateReachableBranches";

type AuthContextProps = {
  auth: AuthModel | undefined;
  saveAuth: (auth: AuthModel | undefined) => void;
  currentUser: UserModel | undefined;
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>;
  /** False until AuthInit finishes cold-start cookie restore (ENG-4781). */
  authReady: boolean;
  markAuthReady: () => void;
  deepLink?: string;
  registerDeepLink: (link: string) => void;
  removeDeepLink: () => void;
  logout: () => void;
  updateToken: () => Promise<void>;
  switchUserBranch: (branchId: string | null) => void;
  switchUserOrganization: (organizationId: string) => Promise<boolean>;
  acceptOrganizationInvite: (
    inviteId: string,
    recaptchaToken: string
  ) => Promise<{ success: true } | { success: false; status?: number }>;
  applyAuthResponse: (auth: AuthModel) => boolean;
};

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  authReady: false,
  markAuthReady: () => {},
  logout: () => {},
  deepLink: undefined,
  registerDeepLink: () => null,
  removeDeepLink: () => null,
  updateToken: async () => {},
  switchUserBranch: () => {},
  switchUserOrganization: async () => false,
  acceptOrganizationInvite: async () => ({ success: false }),
  applyAuthResponse: () => false,
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const readInitialUser = (): UserModel | undefined => {
  const token = authHelper.getAuth()?.access_token;
  if (!token) {
    return undefined;
  }
  try {
    return jwtDecode(token) as UserModel;
  } catch {
    return undefined;
  }
};

const AuthProvider: FC<WithChildren> = ({ children }) => {
  // sessionStorage hydrate (Cmd+R) makes auth available synchronously.
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>(
    readInitialUser
  );
  const [authReady, setAuthReady] = useState(false);
  const [deepLink, setDeepLink] = useState<string | undefined>(undefined);
  const { permission: permissionStore } = useContext(RootContext);

  const clearSessionState = () => {
    setAuth(undefined);
    setCurrentUser(undefined);
    permissionStore.setPermissions([]);
    permissionStore.permissionsLoaded = false;
    permissionStore.setPermissionsLoaded(false);
    setDeepLink(undefined);
  };

  // Cross-tab logout/login: memory auth is synced via BroadcastChannel.
  useEffect(() => {
    return authHelper.subscribeAuth((next) => {
      if (!next) {
        clearSessionState();
        return;
      }
      setAuth(next);
      if (next.access_token) {
        try {
          const decoded = jwtDecode(next.access_token) as UserModel;
          setCurrentUser((prev) => ({
            ...decoded,
            user_branches: prev?.user_branches ?? decoded.user_branches,
            current_branch:
              prev?.current_branch ??
              readStoredCurrentBranch() ??
              decoded.current_branch,
            organization_settings:
              decoded.organization_settings ?? prev?.organization_settings,
          }));
        } catch (error) {
          console.error("Failed to decode cross-tab auth token", error);
        }
      }
    });
  }, []);

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth);
    if (auth) {
      authHelper.setAuth(auth);
    } else {
      authHelper.removeAuth();
    }
  };

  const registerDeepLink = (link: string) => {
    if (link.startsWith("/auth") || link.startsWith("/logout")) return;
    setDeepLink(link);
  };

  const removeDeepLink = () => {
    setDeepLink(undefined);
  };

  const logout = () => {
    trackMerchant(MerchantEvents.LOGOUT);
    reset();
    saveAuth(undefined);
    clearSessionState();
  };

  const updateToken = async () => {
    if (isRefreshBackoffActive()) {
      throw new Error("Token refresh backoff active");
    }

    try {
      const auth = await performTokenRefresh();
      saveAuth(auth);

      if (auth.access_token) {
        const decoded = jwtDecode(auth.access_token) as UserModel;
        setCurrentUser((prev) => ({
          ...decoded,
          user_branches: prev?.user_branches ?? decoded.user_branches,
          current_branch:
            prev?.current_branch ??
            readStoredCurrentBranch() ??
            decoded.current_branch,
          organization_settings:
            decoded.organization_settings ?? prev?.organization_settings,
        }));
      }
    } catch (error) {
      console.error("Failed to refresh token", error);
      throw error;
    }
  };

  const switchUserBranch = async (branchId: string | null) => {
    try {
      const response = await switchBranch(branchId);
      const access_token = response.data.access_token;

      localStorage.setItem("ZD-launcherLabelRemoved", true.toString());
      persistAuthTokens(response.data);
      setAuth(response.data);
      writeStoredCurrentBranch(branchId);

      if (access_token) {
        const decoded = jwtDecode(access_token) as UserModel;
        // Identity-only JWT: keep branch list from prior session / permissions
        // hydrate, and apply the branch the caller just selected.
        setCurrentUser((prev) => ({
          ...decoded,
          user_branches: prev?.user_branches ?? decoded.user_branches,
          current_branch: branchId ?? undefined,
          organization_settings:
            decoded.organization_settings ?? prev?.organization_settings,
        }));
      }

      // Permissions are branch-scoped — refresh immediately for the new branch.
      try {
        const permissionList = await getPermissions();
        const payload = permissionList?.data;
        permissionStore.setPermissionPayload({
          data: [...(payload?.data ?? [])].sort(),
          organization_wide: payload?.organization_wide,
          by_branch: payload?.by_branch,
        });
        permissionStore.permissionsLoaded = true;
        permissionStore.setPermissionsLoaded(true);

        const hydrated = await hydrateReachableBranches(
          payload?.branches,
          payload?.by_branch,
          permissionStore.hasPermission("branches:read")
        );
        setCurrentUser((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            user_branches: hydrated,
            current_branch: branchId ?? undefined,
          };
        });
      } catch (permError) {
        console.error(
          "Failed to refresh permissions after branch switch",
          permError
        );
        permissionStore.setPermissionsLoaded(false);
        permissionStore.permissionsLoaded = false;
      }
    } catch (e) {
      console.error("Error while updating branch", e);
    }
  };

  const applyAuthResponse = (auth: AuthModel, resetBranch = false) => {
    const access_token = auth?.access_token;

    // A response missing access_token must never enter session memory — it
    // would leave a half-logged-in state and confuse refresh / route guards.
    if (!access_token) {
      console.error(
        "[Auth] applyAuthResponse called with no access_token — aborting",
        auth
      );
      return false;
    }

    console.log("[Auth] applyAuthResponse", {
      tokenLength: access_token.length,
    });

    persistAuthTokens(auth);
    setAuth(auth);

    const decoded = jwtDecode(access_token) as UserModel;
    console.log("[Auth] decoded token", decoded);
    if (resetBranch) {
      writeStoredCurrentBranch(null);
    }
    setCurrentUser((prev) => {
      const previousBranches = resetBranch ? undefined : prev;
      return {
        ...decoded,
        user_branches: previousBranches?.user_branches ?? decoded.user_branches,
        current_branch:
          previousBranches?.current_branch ??
          readStoredCurrentBranch() ??
          decoded.current_branch,
        organization_settings:
          decoded.organization_settings ?? prev?.organization_settings,
      };
    });
    return true;
  };

  const switchUserOrganization = async (organizationId: string) => {
    try {
      const response = await switchOrganization(organizationId);
      return applyAuthResponse(response.data, true);
    } catch (e) {
      console.error("Error while switching organization", e);
      return false;
    }
  };

  const acceptOrganizationInvite = async (
    inviteId: string,
    recaptchaToken: string
  ) => {
    try {
      console.log("[Auth] acceptOrganizationInvite calling API", {
        inviteId,
      });
      const response = await acceptInviteAsExistingUser({
        invite_id: inviteId,
        "g-recaptcha-response": recaptchaToken,
      });
      console.log("[Auth] acceptOrganizationInvite response", response.data);

      if (!applyAuthResponse(response.data, true)) {
        return { success: false as const, status: undefined };
      }
      return { success: true as const };
    } catch (e: any) {
      console.error("Error while accepting organization invite", e);
      return { success: false as const, status: e?.response?.status };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        authReady,
        markAuthReady: () => setAuthReady(true),
        logout,
        deepLink,
        registerDeepLink,
        removeDeepLink,
        updateToken,
        switchUserBranch,
        switchUserOrganization,
        acceptOrganizationInvite,
        applyAuthResponse,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const AuthInit: FC<WithChildren> = ({ children }) => {
  const {
    auth,
    currentUser,
    logout,
    setCurrentUser,
    updateToken,
    saveAuth,
    markAuthReady,
  } = useAuth();
  const [showSplashScreen, setShowSplashScreen] = useState(true);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [tokenRefreshed, setTokenRefreshed] = useState(false);
  const { permission: permissionStore, conversionRates: conversionRatesStore } =
    useContext(RootContext);

  useEffect(() => {
    const requestUser = async () => {
      try {
        // Cold start (ENG-4781):
        // 1) sessionStorage (same tab Cmd+R)
        // 2) BroadcastChannel from an already-open tab (new tab)
        // 3) HttpOnly refresh cookie (no sibling tab; skip after explicit logout)
        let access_token = authHelper.getAuth()?.access_token;
        if (!access_token && !tokenRefreshed) {
          const fromOtherTab = await authHelper.waitForAuthFromOtherTabs();
          access_token =
            fromOtherTab?.access_token ?? authHelper.getAuth()?.access_token;

          if (access_token) {
            setTokenRefreshed(true);
            setCurrentUser(jwtDecode(access_token) as UserModel);
            console.log("Session restored from sibling tab on app init");
          } else if (authHelper.hasLogoutIntent()) {
            console.log(
              "Skipping refresh-cookie restore after explicit logout"
            );
            setTokenRefreshed(true);
          } else {
            try {
              setTokenRefreshed(true);
              const refreshed = await performTokenRefresh();
              saveAuth(refreshed);
              access_token = refreshed.access_token;
              if (access_token) {
                setCurrentUser(jwtDecode(access_token) as UserModel);
              }
              console.log("Session restored from refresh cookie on app init");
            } catch (refreshError) {
              console.log("No refreshable session on app init", refreshError);
              setTokenRefreshed(false);
            }
          }
        } else if (access_token && !tokenRefreshed) {
          const tokenExpiryStr = authHelper.getAccessTokenExpiry();

          if (tokenExpiryStr) {
            const tokenExpiry = dayjs(tokenExpiryStr);
            const secondsUntilExpiry = tokenExpiry.diff(dayjs(), "seconds");

            if (secondsUntilExpiry <= 300) {
              console.log(
                "Token expired or expiring soon, refreshing proactively on app init"
              );
              try {
                setTokenRefreshed(true);
                await updateToken();
                console.log("Token refreshed successfully on app init");
                access_token = authHelper.getAuth()?.access_token;
              } catch (refreshError) {
                console.error(
                  "Failed to refresh token on app init",
                  refreshError
                );
                setTokenRefreshed(false);
              }
            }
          }
        }

        access_token = authHelper.getAuth()?.access_token;

        if (currentUser || access_token) {
          if (!permissionStore.isPermissionsLoaded()) {
            if (
              window.location.pathname.search("/invoice-consent") >= 0 ||
              window.location.pathname.startsWith("/payment-link/")
            ) {
              return;
            }
          }
        }

        if (currentUser) {
          return currentUser;
        }
        if (access_token) {
          const decoded = jwtDecode(access_token);
          setCurrentUser(decoded as UserModel);
          return;
        }
      } catch (error) {
        console.error(error);
        if (currentUser) {
          logout();
        }
      } finally {
        setPermissionsLoaded(true);
        setShowSplashScreen(false);
        // AppRoutes must wait for this before treating missing currentUser as
        // signed-out — otherwise Chrome F5 races to /auth (ENG-4781).
        markAuthReady();
      }
    };

    requestUser();
  }, []);

  const conversionRatesFetchingRef = useRef(false);

  useEffect(() => {
    if (
      auth?.access_token &&
      !conversionRatesStore.isLoaded &&
      !conversionRatesFetchingRef.current
    ) {
      conversionRatesFetchingRef.current = true;
      getConversionRates()
        .then((response) => {
          conversionRatesStore.setRates(response.data?.conversion_rates ?? {});
        })
        .catch((error) => {
          console.error("Failed to fetch conversion rates", error);
          conversionRatesFetchingRef.current = false;
        });
    }
  }, [auth?.access_token]);

  if (showSplashScreen || !permissionsLoaded) {
    return <LayoutSplashScreen />;
  }

  return <>{children}</>;
};

export { AuthProvider, AuthInit, useAuth };

/* eslint-enable */
